import { seedFallbackTemplateOnEditor } from '@src/editor/services/applyCaptionsLanguage';
import { resolveCaptionLanguageKey } from '@src/utility/captionLanguage';

import { notifyCaptionFontChange } from './switchCaptionLanguage';

import type { IVideoEditor } from '@src/editor/interface/IVideoEditor';
import type { CaptionFallbackTemplate } from '@src/editor/services/applyCaptionsLanguage';
import type { ToastRequest } from '@src/stores/toast';

/**
 * The CAPTIONS button's first produce — a video with no caption track yet
 * (never produced, or cleared). Plain orchestration, not a hook; kept out of
 * `Transcript.tsx` (its line budget) and shared by its hosts.
 *
 *  1. PLAT-10635 — seed the org's first custom caption template over the
 *     built-in "Impact" default when the video has no template applied.
 *  2. PLAT-11674 — match the font to the language the produce will render.
 *     `KaraokeConfig.language` is the record of the user's pick (written by
 *     the language switch's no-track path) and what `ProduceKaraokeCommand`
 *     reads, so resolving the canonical key off it keeps font and text in
 *     step; the PLAT-11409 correction then swaps a Latin-only font for the
 *     script-capable equivalent (Anton + Japanese was tofu) with the same
 *     passive toast the switch path shows.
 *  3. Produce the track (one undo entry, like the button always did). The
 *     toast waits on it: a failed produce (no active tokens) shows no captions.
 *
 * Returns whether a track was produced.
 */
export function produceFirstCaptions(params: {
  editor: IVideoEditor;
  fallbackTemplate?: CaptionFallbackTemplate;
  transcriptLanguage?: string | null;
  originalVideoLanguage?: string | null;
  showToast: (toast: ToastRequest) => number;
}): boolean {
  const {
    editor,
    fallbackTemplate,
    transcriptLanguage,
    originalVideoLanguage,
    showToast,
  } = params;
  const fontSubstituted = seedFallbackTemplateOnEditor(
    editor,
    fallbackTemplate,
    {
      targetLanguageKey: resolveCaptionLanguageKey(
        editor.getDefaultKaraokeConfig()?.language,
        transcriptLanguage,
        originalVideoLanguage,
      ),
    },
  );
  if (!editor.commandProduceKaraoke({ replaceTrack: true })) return false;
  notifyCaptionFontChange(showToast, fontSubstituted);
  return true;
}
